/**
 * Detecta erros do PostgREST quando tabela/coluna ainda não existe no projeto
 * (migração não aplicada). Evita loops de seed e ruído no console.
 */

const activityTypesTableMissingByOrg = new Set<string>();
const activitySettingsColumnMissingByOrg = new Set<string>();

export function isActivityTypesTableMissingForOrg(orgId: string | null | undefined): boolean {
  if (!orgId) return false;
  return activityTypesTableMissingByOrg.has(orgId);
}

export function isActivitySettingsColumnMissingForOrg(orgId: string | null | undefined): boolean {
  if (!orgId) return false;
  return activitySettingsColumnMissingByOrg.has(orgId);
}

export function markActivityTypesTablePresent(orgId: string): void {
  activityTypesTableMissingByOrg.delete(orgId);
}

export function markActivityTypesTableMissing(orgId: string): void {
  activityTypesTableMissingByOrg.add(orgId);
}

export function markActivitySettingsColumnPresent(orgId: string): void {
  activitySettingsColumnMissingByOrg.delete(orgId);
}

export function markActivitySettingsColumnMissing(orgId: string): void {
  activitySettingsColumnMissingByOrg.add(orgId);
}

function errMsg(e: unknown): string {
  if (!e || typeof e !== "object") return "";
  const o = e as { message?: string; details?: string; hint?: string };
  return `${o.message || ""} ${o.details || ""} ${o.hint || ""}`.toLowerCase();
}

function httpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as { status?: number; statusCode?: number };
  return e.status ?? e.statusCode;
}

/** Tabela não existe, não está no cache do PostgREST ou rota 404. */
export function isSupabaseRelationUnavailable(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; message?: string; status?: number };
  if (httpStatus(error) === 404) return true;
  const m = errMsg(error);
  if (m.includes("schema cache") && m.includes("could not find")) return true;
  if (m.includes("does not exist") && (m.includes("relation") || m.includes("table"))) return true;
  if (e.code === "42P01") return true;
  return false;
}

/** Coluna inexistente ou não selecionável (ex.: activity_settings ainda não migrada). */
export function isSupabaseColumnUnavailable(error: unknown, columnHint?: string): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; status?: number };
  if (httpStatus(error) === 400) {
    const m = errMsg(error);
    if (columnHint && m.includes(columnHint.toLowerCase())) return true;
    if (m.includes("column") && m.includes("does not exist")) return true;
  }
  return false;
}

/** Erro ao ler `organizations.activity_settings` (migração não aplicada ou coluna ausente). */
export function shouldDegradeActivityOrgSettingsFetch(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const m = errMsg(error);
  if (isSupabaseColumnUnavailable(error, "activity_settings")) return true;
  if (httpStatus(error) === 400) {
    if (m.includes("jwt") || m.includes("permission") || m.includes("policy")) return false;
    if (m.includes("activity_settings")) return true;
    if (m.includes("column") && m.includes("does not exist")) return true;
    if (m.includes("schema") && m.includes("cache")) return true;
    // Select só dessa coluna: 400 costuma indicar coluna/schema inexistente no PostgREST
    return true;
  }
  return false;
}
